import React from 'react';
import * as Yup from 'yup';
import { Formik, useFormikContext } from 'formik';
import { Box, Collapse, IconButton, Table, TableBody, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Info } from '@mui/icons-material';

import { Alert, AvailabilityTips, TimeRangeInputValue, Tooltip, FadeAndSlide, FullscreenFormContainer, FullscreenFormFooter, Button } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { TimeRange, WeekDayKeys, ProviderMedicalRole } from '@uplift/types';
import { getAvailabilityTemplate, setAvailabilityTemplate } from '@uplift/api';
import { L10nHTML, useL10n } from '@uplift/hooks';
import { useCurrentuser } from '@uplift/contexts';
import { asyncWait } from '@uplift/utils';

import { AvailabilityFormRow } from './AvailabilityFormRow';
import { useQuery } from '@tanstack/react-query';


type FormValues = Record<WeekDayKeys, TimeRangeInputValue[]>;


const availabilitySchema = Yup.object().shape({
  SUNDAY: Yup.array(),
  MONDAY: Yup.array(),
  TUESDAY: Yup.array(),
  WEDNESDAY: Yup.array(),
  THURSDAY: Yup.array(),
  FRIDAY: Yup.array(),
  SATURDAY: Yup.array(),
});


const useStyles = makeStyles()(theme => ({
  icon: {
    color: theme.palette.info.main,
  },
  tooltipContent: {
    lineHeight: 1.3,
    width: '232px',
    fontSize: '14px',
  },
  tip: {
    backgroundColor: theme.palette.secondary.background
  },
  tipLabel: {
    fontWeight: 'bold',
    paddingRight: theme.spacing(1),
  }
}));


export type FUEAvailabilityFormProps = {
  onSuccess: () => void;
  onBack: () => void;
  idealCaseload?: number;
  style?: React.CSSProperties;
  animateIn?: boolean;
  nextStepActionFooter?: boolean;
};


const calcForDay = (ranges: TimeRange[]) => {
  const _ranges = ranges.filter(r => r.start && r.end);
  const squashed = CalendarDateService.squashTimeRanges(_ranges);
  let hasShortAvailability = false;

  const durations = squashed.reduce((prev, curr) => {
    const tw = CalendarDateService.timeRangeToTimeWindow(curr);

    const duration = tw.end.diff(tw.start, 'minutes');

    if (duration < 60) {
      hasShortAvailability = true;
    }

    return prev + duration;
  }, 0);

  return [ durations, hasShortAvailability ] as const;
};

export const FUEAvailabilityFormContent: React.FC<FUEAvailabilityFormProps> = (props) => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const { classes } = useStyles();
  const formik = useFormikContext<FormValues>();
  const isTherapist = me.medicalRole === ProviderMedicalRole.therapist;

  const d1 = React.useMemo(() => calcForDay(formik.values.SUNDAY), [ formik.values.SUNDAY ]);
  const d2 = React.useMemo(() => calcForDay(formik.values.MONDAY), [ formik.values.MONDAY ]);
  const d3 = React.useMemo(() => calcForDay(formik.values.TUESDAY), [ formik.values.TUESDAY ]);
  const d4 = React.useMemo(() => calcForDay(formik.values.WEDNESDAY), [ formik.values.WEDNESDAY ]);
  const d5 = React.useMemo(() => calcForDay(formik.values.THURSDAY), [ formik.values.THURSDAY ]);
  const d6 = React.useMemo(() => calcForDay(formik.values.FRIDAY), [ formik.values.FRIDAY ]);
  const d7 = React.useMemo(() => calcForDay(formik.values.SATURDAY), [ formik.values.SATURDAY ]);

  const aggregation = React.useMemo(() => [ d1, d2, d3, d4, d5, d6, d7 ], [ d1, d2, d3, d4, d5, d6, d7 ]);
  const hasShortAvailability = React.useMemo(() => aggregation.findIndex(s => s[1]) > -1, [ aggregation ]);
  const hasWarning = isTherapist && hasShortAvailability;
  const hours = React.useMemo(() => aggregation.reduce((p, [ hours ]) => p + hours, 0) / 60, [ aggregation ]);

  return (
    <FullscreenFormContainer>
      <Box style={props.style}>
        <Box>
          <Typography variant="overline" color="secondary">{l10n.schedule.configuration.availability.setYourRecurringAvailability}</Typography>
        </Box>
        <Box mt={1} pb={1}>
          <Typography variant="h4">{l10n.schedule.configuration.availability.whenYouWillBeRegularlyAvailable}</Typography>
        </Box>
        <Box pb={1}>
          <Typography variant="body2" style={{ fontSize: '16px' }}>{l10n.schedule.configuration.availability.availabilityDescription}</Typography>
        </Box>
        <Box className={classes.tip}>
          <Box px={3} py={2}>
            <Typography variant="body1"><span className={classes.tipLabel}>Tip:</span>{l10n.schedule.configuration.availability.tip}</Typography>
          </Box>
        </Box>

        <Collapse in={hasWarning}>
          <Box>
            <Box mt={2}>
              <Alert severity="warning">{l10n.calendar.availability.editForm.lessThenHourWarningTitle}</Alert>
            </Box>
          </Box>
        </Collapse>

        <Box mt={2}>
          <Table size="medium">
            <TableBody>
              {l10n.general.weekday.map(w =>
                <AvailabilityFormRow key={w} title={w} namePrefix={w.toUpperCase() as WeekDayKeys} />
              )}
            </TableBody>
          </Table>
        </Box>
        <Box mt={8} display="flex" justifyContent="center">
          <Box width="350px" display="flex" flexDirection="column" gap="24px">
            <Box display="flex" justifyContent="center" alignItems="center" gap="8px">
              <L10nHTML>{l10n.schedule.configuration.availability.availableHours(hours)}</L10nHTML>
              <Tooltip
                title={(
                  <Typography className={classes.tooltipContent}>
                    {l10n.schedule.configuration.availability.recommendAvailableHours(props.idealCaseload * 1.5)}
                  </Typography>
                )}
                placement="right"
                arrow
              >
                <IconButton className={classes.icon} size="small"><Info /></IconButton>
              </Tooltip>
            </Box>
            {!props.nextStepActionFooter && (
              <>
                <Button fullWidth onClick={formik.handleSubmit} loading={formik.isSubmitting || !props.animateIn} color="secondary" variant="contained" size="large">
                  {l10n.schedule.configuration.availability.finishSetup}
                </Button>
                <Button
                  fullWidth
                  onClick={props.onBack}
                  disabled={formik.isSubmitting || !props.animateIn}
                >
                  {l10n.navigation.back}
                </Button>
              </>
            )}
          </Box>
        </Box>
        {props.nextStepActionFooter && (
          <Box mt={8}>
            <FullscreenFormFooter
              loading={formik.isSubmitting || !props.animateIn}
              onBack={props.onBack}
              nextDisabled={!formik.isValid || hours === 0}
              onNext={formik.handleSubmit}
            />
          </Box>
        )}
      </Box>
      <FadeAndSlide in={props.animateIn} direction="down-up">
        <AvailabilityTips position="fixed" />
      </FadeAndSlide>
    </FullscreenFormContainer>
  );
};


export const FUEAvailabilityForm: React.FC<FUEAvailabilityFormProps> = (props) => {
  const me = useCurrentuser();
  const { data: availabilityTemplate } = useQuery({
    queryKey: [ 'availabilityTemplate', me.id ],
    queryFn: async () => {
      const response = await getAvailabilityTemplate();

      return response.days;
    } });

  const handleSubmit = React.useCallback(async (values: FormValues) => {
    const days = (Object.keys(values) as WeekDayKeys[]).reduce((prev, curr) => ({
      ...prev,
      [curr]: values[curr].map(v => ({ start: v.start, end: v.end })),
    }), {} as Record<WeekDayKeys, TimeRange[]>);

    await Promise.all([
      setAvailabilityTemplate({ days }),
      asyncWait(500),
    ]);

    props.onSuccess();
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={availabilityTemplate ?? {
        SUNDAY: [],
        MONDAY: [],
        TUESDAY: [],
        WEDNESDAY: [],
        THURSDAY: [],
        FRIDAY: [],
        SATURDAY: [],
      } as FormValues}
      validationSchema={availabilitySchema}
      onSubmit={handleSubmit}
    >
      <FUEAvailabilityFormContent {...props} />
    </Formik>
  );
};
